import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, output, signal } from '@angular/core';
import {
    DocGenPdfStatusEnum,
    DocumentBatchItem,
    DocumentBatchItemStatusEnum,
    DocumentBatchStatusEnum,
    DocumentEntity,
} from '@domain';
import { TranslatePipe } from '@ngx-translate/core';
import { RaButtonComponent } from '@shared/components';
import { RaIconComponent } from 'src/app/shared/components/ra-icon/ra-icon.component';
import { RaTooltipDirective } from 'src/app/shared/directives';
import { RaModalComponent } from 'src/app/shared/components/ra-modal/ra-modal.component';
import { FormatFileSizePipe } from 'src/app/shared/pipes/format-file-size.pipe';
import { computeDocumentSizeGB } from 'src/app/shared/pipes/documents/document-size.pipe';
import { DocumentBatchService } from 'src/app/shared/services/document-batch.service';

/**
 * "Baixar em lote" em dois passos: escolher os documentos e acompanhar a geração.
 *
 * O modal não guarda o andamento — quem é dono do pacote é o `DocumentBatchService`, que
 * vive fora da tela. Por isso minimizar (ou fechar no meio) não interrompe nada, e reabrir
 * mostra exatamente onde a geração está.
 */
@Component({
    selector: 'modal-document-batch',
    templateUrl: 'modal-document-batch.component.html',
    styleUrl: 'modal-document-batch.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        DatePipe,
        TranslatePipe,
        RaModalComponent,
        RaButtonComponent,
        RaIconComponent,
        RaTooltipDirective,
        FormatFileSizePipe,
    ],
})
export class ModalDocumentBatchComponent {
    public orderId = input.required<string>();
    /** Documentos do serviço, como a tela já os tem: o modal não rebusca nada. */
    public documents = input<DocumentEntity[]>([]);
    public close = output<void>();

    protected readonly batchService = inject(DocumentBatchService);
    protected readonly itemStatus = DocumentBatchItemStatusEnum;

    /** Ids DESmarcados. Guardar o avesso mantém "tudo marcado" como estado inicial. */
    private readonly _unchecked = signal<Set<string>>(new Set());
    protected readonly query = signal('');

    /** A lista dos que já têm PDF começa fechada: é a maioria e não pede decisão. */
    private readonly _showReady = signal(false);

    /**
     * Filtrar abre a lista dos prontos por conta própria: quem procurou um documento pelo
     * nome quer vê-lo, esteja ele em qual grupo estiver.
     */
    protected readonly readyExpanded = computed(
        () => this._showReady() || this.query().trim().length > 0
    );

    /** O cabeçalho do grupo só faz sentido com a lista aberta por clique. */
    protected readonly showReady = computed(() => this._showReady());

    protected toggleReady(): void {
        this._showReady.update(current => !current);
    }

    /** O passo 2 não é escolha do modal: é o pacote existir. */
    protected readonly step = computed<'select' | 'progress'>(() =>
        this.batchService.running() || this.batchService.ready() || this.batchService.starting()
            ? 'progress'
            : 'select'
    );

    /**
     * Filtrar por nome só ajuda quando a lista é grande.
     *
     * Num serviço de três documentos o campo é só mais uma coisa na tela — o mesmo
     * motivo pelo qual "selecionar todos" some quando existe um documento só.
     */
    protected readonly showFilter = computed(() => this.documents().length > 8);

    protected readonly visibleDocuments = computed(() => {
        const query = this.query().trim().toLowerCase();
        const documents = this.documents();
        if (!query) return documents;

        return documents.filter(document => (document.title ?? '').toLowerCase().includes(query));
    });

    /**
     * Os dois grupos do passo 1: o que o pacote vai gerar e o que já tem PDF.
     *
     * Um documento com PDF em dia entra no ZIP direto, sem render e sem cobrar — é a
     * regra do `requestPDF`, que o lote herda. Numa lista de 42 isso era invisível: a
     * pessoa via "Gerar 42 PDFs" sem saber que só 30 seriam de fato gerados.
     */
    protected readonly pendingDocuments = computed(() =>
        this.visibleDocuments().filter(document => !this._hasPdf(document))
    );

    protected readonly readyDocuments = computed(() =>
        this.visibleDocuments().filter(document => this._hasPdf(document))
    );

    /** Quantos dos MARCADOS vão passar pelo engine: é o número do botão. */
    protected readonly toGenerateCount = computed(() => {
        const selected = new Set(this.selectedIds());

        return this.documents().filter(
            document => selected.has(document._id) && !this._hasPdf(document)
        ).length;
    });

    protected readonly selectedReadyCount = computed(
        () => this.selectedIds().length - this.toGenerateCount()
    );

    /** Mesma conta do servidor: PDF concluído E com arquivo no lugar. */
    private _hasPdf(document: DocumentEntity): boolean {
        return document.pdfStatus === DocGenPdfStatusEnum.COMPLETED && Boolean(document.pdfURL);
    }

    protected groupSizeGB(documents: DocumentEntity[]): number {
        return documents.reduce(
            (total, document) => total + computeDocumentSizeGB(document.sessions),
            0
        );
    }

    protected readonly selectedIds = computed(() =>
        this.documents()
            .map(document => document._id)
            .filter(id => !this._unchecked().has(id))
    );

    protected readonly allSelected = computed(
        () => this.selectedIds().length === this.documents().length
    );

    protected readonly selectedSizeGB = computed(() => {
        const selected = new Set(this.selectedIds());

        return this.documents()
            .filter(document => selected.has(document._id))
            .reduce((total, document) => total + computeDocumentSizeGB(document.sessions), 0);
    });

    /**
     * A lista do passo 2 em ordem de atenção: quem ainda não terminou fica em cima.
     *
     * Num pacote de 42 documentos o que falta some no fim da rolagem depois de alguns
     * minutos — exatamente quando ele é a única coisa que importa. Concluído desce;
     * falha fica no meio, porque ainda pede uma decisão (tentar de novo ou deixar).
     */
    protected readonly orderedItems = computed(() => {
        const items = this.batchService.batch()?.items ?? [];

        return items
            .map((item, index) => ({ item, index }))
            .sort((a, b) => this._rank(a.item) - this._rank(b.item) || a.index - b.index)
            .map(entry => entry.item);
    });

    /** Em render, na fila, falhou, pronto. */
    private _rank(item: DocumentBatchItem): number {
        if (item.status === DocumentBatchItemStatusEnum.COMPLETED) return 3;
        if (item.status === DocumentBatchItemStatusEnum.ERROR) return 2;

        return this.batchService.isRendering(item.documentId) ? 0 : 1;
    }

    /**
     * "Tentar de novo" também vale depois do pacote fechado: o ZIP é montado de novo com
     * o documento que faltava. Num pacote cancelado, não — ali a decisão já foi outra.
     *
     * Durante a MONTAGEM não aparece para ninguém: o arquivo está sendo costurado com o
     * que já existe, e devolver um item à fila no meio disso é pedir uma corrida entre o
     * pacote que fecha e o documento que volta a ser gerado. Quem perdeu um documento
     * tenta de novo quando o pacote fica pronto.
     */
    protected readonly canRetry = computed(() => {
        if (this.batchService.zipping()) return false;

        const status = this.batchService.batch()?.status;

        return (
            status === DocumentBatchStatusEnum.GENERATING ||
            status === DocumentBatchStatusEnum.PARTIAL ||
            status === DocumentBatchStatusEnum.ERROR
        );
    });

    protected readonly failedItems = computed(
        () =>
            this.batchService
                .batch()
                ?.items?.filter(item => item.status === DocumentBatchItemStatusEnum.ERROR) ?? []
    );

    // * ── Passo 1 ──────────────────────────────────────────────────────────────

    protected isChecked(documentId: string): boolean {
        return !this._unchecked().has(documentId);
    }

    protected toggle(documentId: string): void {
        const next = new Set(this._unchecked());
        next.has(documentId) ? next.delete(documentId) : next.add(documentId);

        this._unchecked.set(next);
    }

    /** O "selecionar todos" olha só o que está à vista: filtrou, marca o que filtrou. */
    protected toggleAll(): void {
        if (this.allSelected()) {
            this._unchecked.set(new Set(this.visibleDocuments().map(document => document._id)));
            return;
        }

        this._unchecked.set(new Set());
    }

    protected sizeOf(document: DocumentEntity): number {
        return computeDocumentSizeGB(document.sessions);
    }

    /** Nada a gerar: o pacote é só a montagem do ZIP, e o botão não pode dizer "Gerar 0". */
    protected generateLabelKey(): string {
        if (this.toGenerateCount() === 0) return 'order.documentBatch.generateReadyOnly';

        return this._plural('order.documentBatch.generate', this.toGenerateCount());
    }

    /** Nada a gerar: o pacote não cobra nada, e prometer crédito seria mentira. */
    protected chargeKey(): string {
        if (this.toGenerateCount() === 0) return 'order.documentBatch.chargeNone';

        return this._plural('order.documentBatch.charge', this.toGenerateCount());
    }

    protected readySummaryKey(): string {
        return this._plural('order.documentBatch.readySummary', this.readyDocuments().length);
    }

    protected groupCountKey(count: number): string {
        return this._plural('order.documentBatch.groupCount', count);
    }

    protected selectedSummaryKey(): string {
        return this._plural('order.documentBatch.selectedSummary', this.selectedIds().length);
    }

    /**
     * Chave no singular quando é um só.
     *
     * Sem isto o botão dizia "Gerar 1 PDFs" — o caso mais comum depois da primeira
     * rodada, quando a pessoa edita um documento e volta para refazer o pacote.
     */
    private _plural(key: string, count: number): string {
        return count === 1 ? `${key}One` : key;
    }

    protected confirm(): void {
        const ids = this.selectedIds();
        if (!ids.length) return;

        this.batchService.start(this.orderId(), ids);
    }

    // * ── Passo 2 ──────────────────────────────────────────────────────────────

    protected minimize(): void {
        this.batchService.minimize();
        this.close.emit();
    }

    protected cancel(): void {
        this.batchService.cancel();
        this.close.emit();
    }

    protected download(): void {
        this.batchService.downloadZip();
    }

    /** Tira este documento do pacote e segue com os outros. */
    protected skipItem(documentId: string): void {
        this.batchService.skipItem(documentId);
    }

    protected retryItem(documentId: string): void {
        this.batchService.retryItem(documentId);
    }

    /** `skipped` é o item que a própria pessoa tirou: dizer "falhou" ali seria mentira. */
    protected itemErrorKey(item: DocumentBatchItem): string {
        return item.error === 'skipped'
            ? 'order.documentBatch.itemSkipped'
            : 'order.documentBatch.itemFailed';
    }

    protected titleKey(): string {
        if (this.step() === 'select') return 'order.documentBatch.selectTitle';

        return this.batchService.ready()
            ? 'order.documentBatch.readyTitle'
            : 'order.documentBatch.progressTitle';
    }
}
